import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';

import { IconName } from '../_shared/models/icon.model';
import { ILink } from '../_shared/models/link.model';
import { IMenuCart, IMenuGroup } from '../_shared/models/navigation/menu/menu-group.model';
import { NavigationMenuDesktopComponent } from './navigation-menu/navigation-menu-desktop/navigation-menu-desktop.component';
import { NavigationMenuDropdownComponent } from './navigation-menu/navigation-menu-dropdown/navigation-menu-dropdown.component';
import { NavigationMenuMobileComponent } from './navigation-menu/navigation-menu-mobile/navigation-menu-mobile.component';
import { NavigationService } from './navigation.service';

@Component({
    selector: 'sirius-navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NavigationMenuDesktopComponent, NavigationMenuDropdownComponent, NavigationMenuMobileComponent],
})
export class NavigationComponent {
    private readonly _navService = inject(NavigationService);

    readonly menuGroups = input<IMenuGroup[]>([]);
    readonly searchPageUrl = input('');
    readonly logos = input<string[]>([]);
    readonly profileEnabled = input(true);
    // TODO - profile/cart data should come in a more generic form, following a generic menu model.
    readonly profileMenuOptions = input<{ icon: IconName; link: ILink }[] | undefined>(undefined);
    readonly cartData = input<IMenuCart | undefined>(undefined);
    readonly ctaButton = input<ILink | undefined>(undefined);
    readonly siteRoot = input<ILink>({ title: '', href: '' });

    readonly profileClicked = output<void>();

    readonly mobileMenuOpen = signal(false);
    readonly enteredMenuItem = signal<number | null>(null);

    // Set to true when the user clicks the already-active menu trigger to force
    // the dropdown closed even though enteredMenuItem is still non-null. Cleared
    // on hover/leave so subsequent hovers behave normally.
    private readonly _forceClosed = signal(false);

    readonly dropdownOpen = computed(() => this.enteredMenuItem() !== null && !this._forceClosed());

    readonly hasItems = computed<boolean>(() => {
        const idx = this.enteredMenuItem();
        if (idx === null) return false;
        return this._navService.hasItems(this.menuGroups()[idx]);
    });

    private _lastClickedMenuItem: number | null = null;

    onMouseLeave(): void {
        this.enteredMenuItem.set(null);
        this._forceClosed.set(false);
    }

    onMenuItemEntered(index: number): void {
        this.enteredMenuItem.set(index);
        this._forceClosed.set(false);
    }

    onMenuItemClicked(index: number): void {
        this.enteredMenuItem.set(index);

        if (this._lastClickedMenuItem === index) {
            this._forceClosed.update((closed) => !closed);
        } else {
            this._forceClosed.set(false);
        }

        this._lastClickedMenuItem = index;
    }

    onDropdownClicked(): void {
        this._forceClosed.set(true);
    }

    onMobileMenuToggle(open: boolean): void {
        this.mobileMenuOpen.set(open);
    }
}
